export type BrowserLogLevel = "info" | "warn" | "error";

export interface BrowserLogEntry {
  id: string;
  level: BrowserLogLevel;
  message: string;
  createdAt: number;
}

const MAX_LOGS = 200;
const logs: BrowserLogEntry[] = [];
let installed = false;
let restoreConsole: (() => void) | null = null;

const SECRET_PATTERNS = [
  /authorization\s*[:=]\s*bearer\s+[^\s,;]+/gi,
  /(?:api[-_ ]?key|authorization|bearer|token|cookie|client[-_ ]?secret)\s*[:=]\s*[^\s,;]+/gi,
  /sk-[a-z0-9_-]{12,}/gi,
];

export function redactBrowserLogText(value: unknown): string {
  let text: string;
  if (value instanceof Error) text = `${value.name}: ${value.message}\n${value.stack ?? ""}`;
  else if (typeof value === "string") text = value;
  else {
    try { text = JSON.stringify(value); }
    catch { text = String(value); }
  }
  for (const pattern of SECRET_PATTERNS) text = text.replace(pattern, "[REDACTED]");
  return text.slice(0, 16_384);
}

export function recordBrowserLog(level: BrowserLogLevel, ...values: unknown[]) {
  logs.push({
    id: crypto.randomUUID(),
    level,
    message: values.map(redactBrowserLogText).join(" "),
    createdAt: Date.now(),
  });
  if (logs.length > MAX_LOGS) logs.splice(0, logs.length - MAX_LOGS);
}

export function listBrowserLogs(): BrowserLogEntry[] {
  return logs.map((entry) => ({ ...entry }));
}

export function clearBrowserLogs() { logs.splice(0, logs.length); }

export function installBrowserLogCapture(): () => void {
  if (installed || typeof window === "undefined") return () => undefined;
  installed = true;
  const onError = (event: ErrorEvent) => recordBrowserLog("error", event.error ?? event.message);
  const onRejection = (event: PromiseRejectionEvent) => recordBrowserLog("error", "Unhandled rejection", event.reason);
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  const originalWarn = console.warn;
  const originalError = console.error;
  console.warn = (...values: unknown[]) => { recordBrowserLog("warn", ...values); originalWarn.apply(console, values); };
  console.error = (...values: unknown[]) => { recordBrowserLog("error", ...values); originalError.apply(console, values); };
  restoreConsole = () => { console.warn = originalWarn; console.error = originalError; };
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    restoreConsole?.(); restoreConsole = null;
    installed = false;
  };
}
